import { type FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createClassifier } from '../api';
import Header from '../components/Header';

interface SearchResult {
  headline_id: number;
  title: string;
  url: string;
  source: string;
  distance: number;
  matchType: string;
}

interface SearchResponse {
  query: string;
  maxDistance: number;
  searchType: string;
  results: SearchResult[];
}

export default function ClassifierCreatePage() {
  const navigate = useNavigate();
  const [classifierName, setClassifierName] = useState('');
  const [query, setQuery] = useState('');
  const [maxDistance, setMaxDistance] = useState('0.5');
  const [searchType, setSearchType] = useState('content');
  const [results, setResults] = useState<SearchResult[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [polarities, setPolarities] = useState<
    Map<number, 'positive' | 'negative'>
  >(new Map());

  const handleSearch = async (e: FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        q: query.trim(),
        maxDistance,
        searchType,
      });
      const res = await fetch(`/api/embedding-search?${params}`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Request failed: ${res.status}`);
      }
      const data: SearchResponse = await res.json();
      setResults(data.results);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed.');
    } finally {
      setLoading(false);
    }
  };

  const togglePolarity = (
    headlineId: number,
    polarity: 'positive' | 'negative',
  ) => {
    setPolarities((prev) => {
      const next = new Map(prev);
      if (next.get(headlineId) === polarity) {
        next.delete(headlineId);
      } else {
        next.set(headlineId, polarity);
      }
      return next;
    });
  };

  const positiveCount = Array.from(polarities.values()).filter(
    (p) => p === 'positive',
  ).length;
  const negativeCount = Array.from(polarities.values()).filter(
    (p) => p === 'negative',
  ).length;

  const handleCreate = async () => {
    if (!classifierName.trim() || positiveCount === 0 || negativeCount === 0)
      return;
    setCreating(true);
    setError(null);
    try {
      const examples = Array.from(polarities.entries()).map(
        ([headlineId, polarity]) => ({ headlineId, polarity }),
      );
      await createClassifier(classifierName.trim(), examples);
      navigate('/classifiers');
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to train classifier.',
      );
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="page-filter-create">
      <Header />
      <Link className="back-link" to="/classifiers">
        &larr; Back to classifiers
      </Link>

      <h2>Create Classifier</h2>

      <div className="filter-name-row">
        <label>
          Classifier name
          <input
            type="text"
            value={classifierName}
            onChange={(e) => setClassifierName(e.target.value)}
            placeholder="e.g. AI research"
            className="filter-name-input"
          />
        </label>
      </div>

      <h3>Search for training examples</h3>
      <form onSubmit={handleSearch} className="search-form">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search text..."
          className="search-input"
        />
        <label className="distance-label">
          Max distance
          <input
            type="number"
            step="0.01"
            min="0"
            max="2"
            value={maxDistance}
            onChange={(e) => setMaxDistance(e.target.value)}
            className="distance-input"
          />
        </label>
        <label className="distance-label">
          Search in
          <select
            value={searchType}
            onChange={(e) => setSearchType(e.target.value)}
            className="search-type-select"
          >
            <option value="both">Title + Content</option>
            <option value="title">Title only</option>
            <option value="content">Content only</option>
          </select>
        </label>
        <button type="submit" disabled={loading || !query.trim()}>
          {loading ? 'Searching...' : 'Search'}
        </button>
      </form>

      {error && <p className="error">{error}</p>}

      {results !== null && (
        <div className="search-results">
          <p className="result-count">{results.length} results</p>
          <table>
            <thead>
              <tr>
                <th>+</th>
                <th>-</th>
                <th>#</th>
                <th>Dist</th>
                <th>Headline</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r, i) => (
                <tr key={`${r.headline_id}-${i}`}>
                  <td className="col-polarity">
                    <input
                      type="checkbox"
                      checked={polarities.get(r.headline_id) === 'positive'}
                      onChange={() => togglePolarity(r.headline_id, 'positive')}
                    />
                  </td>
                  <td className="col-polarity">
                    <input
                      type="checkbox"
                      checked={polarities.get(r.headline_id) === 'negative'}
                      onChange={() => togglePolarity(r.headline_id, 'negative')}
                    />
                  </td>
                  <td className="col-rank">{i + 1}</td>
                  <td className="col-dist">{r.distance.toFixed(4)}</td>
                  <td>
                    <a href={r.url} target="_blank" rel="noreferrer">
                      {r.title}
                    </a>
                  </td>
                  <td className="col-source">{r.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {polarities.size > 0 && (
        <div className="filter-create-actions">
          <p>
            {polarities.size} example{polarities.size !== 1 ? 's' : ''} selected
            ({positiveCount} positive, {negativeCount} negative)
          </p>
          <button
            type="button"
            className="save-btn"
            disabled={
              creating ||
              !classifierName.trim() ||
              positiveCount === 0 ||
              negativeCount === 0
            }
            onClick={handleCreate}
          >
            {creating ? 'Training...' : 'Train & Save'}
          </button>
        </div>
      )}
    </div>
  );
}
